import { useState } from "react";
import { useT } from "../../../../i18n/I18nProvider";
import { Card, CardHead, PillTabs } from "../../../ui";
import { AreaChart } from "../../../ui/Sparkline";
import type { DashMonth } from "../../../../lib/dashboard";

/** How much of the twelve months to draw. A tighter window is not a different
 *  query - the payload already carries the year - so switching costs nothing
 *  and never disagrees with the KPI tiles above, which read the same array. */
const RANGES = ["3", "6", "12"] as const;
type Range = (typeof RANGES)[number];

/**
 * Invoiced against collected.
 *
 * Two lines rather than one: the solid curve is what was billed, the dashed one
 * is what actually arrived. A single revenue line hides the only thing this
 * chart can tell an operator that the KPI tile cannot - whether the gap between
 * the two is widening.
 *
 * The header used to carry a share button and a date-range button, neither of
 * which did anything. They are gone; the range tabs that replaced them are the
 * one control here that has an answer to give. A page-wide date range already
 * exists at the top of the overview, and a second one on a single card would be
 * two controls competing to own the same question.
 */
export function RevenueCard({ series }: { series: DashMonth[] }) {
  const t = useT();
  const [range, setRange] = useState<Range>("12");

  const shown = series.slice(-Number(range));
  const invoiced = shown.map((m) => m.invoicedCents / 100);
  const collected = shown.map((m) => m.collectedCents / 100);

  return (
    <Card>
      <CardHead
        title={t("dash.revenue.title")}
        // The window is named on the tabs beside it; saying "last 12 months"
        // here as well is the same fact twice and costs a line of wrapping.
        sub={t("dash.revenue.sub")}
        actions={
          <PillTabs
            small
            value={range}
            onChange={setRange}
            tabs={RANGES.map((r) => ({ value: r, label: t("dash.revenue.months", { n: Number(r) }) }))}
          />
        }
      />
      <div className="crm-chart">
        <AreaChart values={invoiced} compare={collected} color="var(--color-primary)" />
      </div>
      <div className="crm-legend">
        <span>
          <i style={{ background: "var(--color-primary)" }} />
          {t("dash.revenue.invoiced")}
        </span>
        <span>
          <i style={{ background: "var(--color-muted-foreground)" }} />
          {t("dash.revenue.collected")}
        </span>
      </div>
    </Card>
  );
}
